// Cliente HTTP del framework de rollout (panel comercio). axiosInstance ya estampa
// Authorization + id_comercio; el lambda resuelve identidad desde el JWT.
//
// Estos endpoints NUNCA se esperan en un handler de UI: el hook hidrata en background
// y el outbox los drena fire-and-forget.

import axiosInstance from './axiosInstance';
import type {
  FeatureBehavior,
  FeatureSwitchResult,
  FeatureTelemetryEvent,
  FeatureVariant,
  FeatureVersionResponse,
} from '@utils/featureRollout/types';

const TELEMETRY_BATCH_MAX = 50;

// GET /v1/feature-version?feature_key=… → resolución vigente para el comercio del JWT.
export async function fetchFeatureVersion(featureKey: string): Promise<FeatureVersionResponse> {
  const { data } = await axiosInstance.get<FeatureVersionResponse>('/v1/feature-version', {
    params: { feature_key: featureKey },
  });
  return data;
}

// PUT /v1/feature-version → cambia/revierte la variante (solo root). No-root devuelve
// { ok:false, reason:'REQUIRES_ROOT' } con 200, así que lo tratamos como resultado, no error.
//
// El proxy del panel ramifica por intención (contrato §4): los triggers
// `compare_peek`/`compare_return`/`revert_commit` los consume el backoffice para decidir si
// es comparación (no toca current_variant) o rechazo (cambia membresía + backoff). Las ramas
// `opt_out` (behavior) y `snooze` (snoozeDays) NO requieren to_variant.
export async function putFeatureVersion(args: {
  featureKey: string;
  toVariant?: FeatureVariant;
  fromVariant?: FeatureVariant;
  trigger?: string;
  behavior?: FeatureBehavior;
  snoozeDays?: number;
}): Promise<FeatureSwitchResult> {
  const { data } = await axiosInstance.put<FeatureSwitchResult>('/v1/feature-version', {
    feature_key: args.featureKey,
    // to_variant es opcional: opt_out/snooze no cambian variante (el proxy lo permite null).
    to_variant: args.toVariant ?? null,
    from_variant: args.fromVariant,
    trigger: args.trigger,
    behavior: args.behavior,
    snooze_days: args.snoozeDays,
  });
  return { ok: Boolean(data?.ok), reason: data?.reason };
}

// POST /v1/feature-telemetry → batch (<=50) de eventos. Idempotente por event_id (ULID).
export async function postFeatureTelemetry(events: FeatureTelemetryEvent[]): Promise<void> {
  if (events.length === 0) return;
  const batch = events.slice(0, TELEMETRY_BATCH_MAX);
  await axiosInstance.post('/v1/feature-telemetry', { events: batch });
}

export { TELEMETRY_BATCH_MAX };
